<template>
  <div class="main-contents">
    <div class="page-header">
      <div class="row align-items-center">
        <div class="col-md-7">
          <h1 class="page-title">Processing Meta Connection</h1>
        </div>
      </div>
    </div>

    <div class="main-body">
      <div class="container-fluid">
        <div class="row">
          <div class="col-md-8 offset-md-2">
            <div class="card text-center">
              <div class="card-body p-5">
                <!-- Success State -->
                <div v-if="connectionStatus === 'success'" class="success-state">
                  <div class="success-icon mb-4">
                    <i class="fa fa-check-circle text-success" style="font-size: 4rem;"></i>
                  </div>
                  <h2 class="mb-3">Connection Successful!</h2>
                  <p class="text-muted mb-4">
                    Your Meta account has been successfully connected to Dealersgear Inc. You can now manage your
                    WhatsApp Business Account from this platform.
                  </p>
                  <div class="connected-details p-4 bg-light rounded mb-4 text-left">
                    <h5 class="mb-3">Connected Account Information:</h5>
                    <p>
                      <strong>Business Name:</strong>
                      <br />
                      {{ businessInfo?.name || 'Loading...' }}
                    </p>
                    <p class="mt-2">
                      <strong>WABA ID:</strong>
                      <br />
                      {{ businessInfo?.waba_id || 'Loading...' }}
                    </p>
                    <p class="mt-2" v-if="businessInfo?.whatsapp_number">
                      <strong>WhatsApp Number:</strong>
                      <br />
                      {{ businessInfo?.whatsapp_number }}
                    </p>
                  </div>
                  <router-link to="/meta-connect" class="btn btn-primary">
                    Back to Meta Settings
                  </router-link>
                </div>

                <!-- Loading State -->
                <div v-else-if="connectionStatus === 'loading'" class="loading-state">
                  <div class="spinner mb-4">
                    <div class="spinner-border" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>
                  </div>
                  <h2 class="mb-3">Processing Your Connection</h2>
                  <p class="text-muted">
                    Please wait while we connect your Meta account...
                  </p>
                </div>

                <!-- Error State -->
                <div v-else-if="connectionStatus === 'error'" class="error-state">
                  <div class="error-icon mb-4">
                    <i class="fa fa-times-circle text-danger" style="font-size: 4rem;"></i>
                  </div>
                  <h2 class="mb-3">Connection Failed</h2>
                  <p class="text-muted mb-4">
                    {{ errorMessage || 'An error occurred while connecting your Meta account. Please try again.' }}
                  </p>
                  <router-link to="/meta-connect" class="btn btn-primary">
                    Try Again
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MetaCallback',
  data() {
    return {
      connectionStatus: 'loading', // 'loading', 'success', 'error'
      businessInfo: null,
      errorMessage: null
    };
  },
  mounted() {
    this.processCallback();
    window.showHideMainLoader(false);
  },
  methods: {
    processCallback() {
      // Get URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const error = urlParams.get('error');
      const errorDescription = urlParams.get('error_description');

      // If there's an error in the callback
      if (error) {
        this.connectionStatus = 'error';
        this.errorMessage = errorDescription || 'Connection was denied or cancelled.';
        return;
      }

      // If we have an authorization code, use demo data for now
      if (code) {
        // Demo business data (temporary for UI/demo purposes)
        const demoBusiness = {
          name: 'Plan D Studios',
          waba_id: '1234567890',
          whatsapp_number: '+923465054043'
        };

        // Simulate async processing and store demo connection
        setTimeout(() => {
          localStorage.setItem(
            'metaConnection',
            JSON.stringify({ status: 'connected', business: demoBusiness })
          );

          this.businessInfo = demoBusiness;
          this.connectionStatus = 'success';
        }, 800);

        return;

        // To use the real backend exchange, uncomment the line below
        // this.exchangeCodeForToken(code);
      } else {
        // Demo: treat missing code as a demo redirect and show demo business
        const demoBusiness = {
          name: 'plan d studios',
          waba_id: '1234567890',
          whatsapp_number: '+923465054043'
        };

        setTimeout(() => {
          localStorage.setItem(
            'metaConnection',
            JSON.stringify({ status: 'connected', business: demoBusiness })
          );

          this.businessInfo = demoBusiness;
          this.connectionStatus = 'success';
        }, 600);
      }
    },

    exchangeCodeForToken(code) {
      // Call your backend API to exchange the code for tokens
      // POST /api/meta/callback
      // This endpoint should:
      // 1. Exchange the code for access tokens
      // 2. Fetch business information
      // 3. Store the tokens securely
      // 4. Return the connected business information

      // For now, we'll simulate the API call
      fetch('/api/meta/callback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ code })
      })
        .then(response => {
          if (!response.ok) {
            throw new Error('Failed to process callback');
          }
          return response.json();
        })
        .then(data => {
          // Store the connection info
          localStorage.setItem(
            'metaConnection',
            JSON.stringify({
              status: 'connected',
              business: data.business
            })
          );

          this.businessInfo = data.business;
          this.connectionStatus = 'success';
        })
        .catch(error => {
          console.error('Error:', error);
          this.connectionStatus = 'error';
          this.errorMessage =
            error.message || 'An error occurred while processing your connection.';
        });
    }
  }
};
</script>

<style scoped>
.main-body {
  padding: 60px 0;
}

.success-state,
.error-state,
.loading-state {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.spinner {
  display: flex;
  justify-content: center;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
  border-width: 0.3rem;
}

.connected-details {
  text-align: left;
}

.connected-details p {
  margin-bottom: 0;
  line-height: 1.6;
}
</style>
